import React, { useState } from 'react';
import useConfirm from '../Components/Modals/useConfirm';
import { useNavigate } from 'react-router-dom';
import useSoloStore, { BOT_CONFIGS } from '../stores/soloStore';
import { submitFlag, batchReReview, loadAllowlist } from '../Services/soloFlagService';
import { useDictionary } from '../hooks/useDictionary';
import DictionarySheet from '../Components/DictionarySheet';
import { BotMascot, MascotAvatar, useMyMascot } from '../Components/Mascot';

const CATEGORY_LABELS = {
  name: 'Name',
  animal: 'Animal',
  place: 'Place',
  thing: 'Thing',
};

function scoreColor(score) {
  if (score >= 10) return 'text-green-700 font-bold';
  if (score > 0)   return 'text-emerald-500 font-semibold';
  if (score < 0)   return 'text-red-600 font-bold';
  return 'text-red-500';
}

export default function SoloRoundResults() {
  const [confirmDialog, ask] = useConfirm();
  const navigate = useNavigate();
  const {
    userScore, botScore, roundHistory, currentRound, totalRounds,
    selectedBot, categories, nextRound, endGame, updateLastRoundScores,
  } = useSoloStore();

  const [flagged, setFlagged]         = useState(new Set());
  const [reReviewing, setReReviewing] = useState(false);
  const [lookupWord, setLookupWord]   = useState(null);
  const { lookup, result: dictResult, loading: dictLoading, error: dictError, clear } = useDictionary();

  function handleLookup(word, category) {
    if (!word?.trim()) return;
    setLookupWord(word.trim());
    lookup(word.trim(), category);
  }

  function handleCloseLookup() {
    setLookupWord(null);
    clear();
  }

  async function handleQuit() {
    const ok = await ask({
      title: 'End the game here?',
      message: 'The current scores count as the final result.',
      confirmLabel: 'End game',
      cancelLabel: 'Keep playing',
    });
    if (!ok) return;
    const result = userScore > botScore ? 'win' : userScore < botScore ? 'loss' : 'tie';
    endGame(result);
    useSoloStore.setState({ gamePhase: 'finished' });
  }

  async function handleFlag(category) {
    if (flagged.has(category)) return;
    const answer = lastRound?.userAnswers?.[category]?.trim() || '';
    try {
      // Only mark it as flagged once the write actually succeeds — marking
      // it first meant an offline browser or a denied/failed write still
      // showed "Flagged" with no retry and no way for the admin to ever
      // see the dispute. (Same fix as mobile's app/solo/results.tsx.)
      await submitFlag({ answer, category, letter: lastRound?.letter || '', botKey: selectedBot });
      setFlagged((prev) => new Set([...prev, category]));
    } catch (err) {
      console.error('[SoloRoundResults] submitFlag failed:', err);
      alert("Couldn't submit dispute. Please check your connection and try again.");
    }
  }

  async function handleContinue() {
    if (flagged.size > 0) {
      setReReviewing(true);
      try {
        const allowlist = await loadAllowlist().catch(() => ({}));
        const letter = lastRound?.letter || '';
        const flaggedItems = [...flagged].map((cat) => ({
          category: cat,
          answer: lastRound?.userAnswers?.[cat]?.trim() || '',
        }));
        const updated = await batchReReview(letter, flaggedItems, allowlist);
        if (Object.keys(updated).length > 0) {
          updateLastRoundScores(updated);
        }
      } catch {
        // Re-review failed — proceed anyway
      } finally {
        setReReviewing(false);
      }
    }
    nextRound();
  }

  const botConfig   = BOT_CONFIGS[selectedBot] || {};
  const lastRound   = roundHistory[roundHistory.length - 1];
  const isLastRound = currentRound >= totalRounds;
  const diff        = userScore - botScore;

  const specialEffect = lastRound?.specialEffect;
  const myMascot = useMyMascot();
  const specialIsPenalty = /penalt|lose|lost|-\s?\d|zero/i.test(`${specialEffect?.title} ${specialEffect?.description}`);

  // Effect container style based on type
  const effectStyle = {
    first_double:   { border: 'border-emerald-400', bg: 'bg-emerald-50', text: 'text-emerald-700' },
    all_or_nothing: { border: 'border-red-400',     bg: 'bg-red-50',     text: 'text-red-700' },
    wildcard:       { border: 'border-purple-400',  bg: 'bg-purple-50',  text: 'text-purple-700' },
    time_zones:     { border: 'border-yellow-400',  bg: 'bg-yellow-50',  text: 'text-yellow-700' },
    double_jeopardy:{ border: 'border-red-400',     bg: 'bg-red-50',     text: 'text-red-700' },
  }[specialEffect?.type] || { border: 'border-gray-300', bg: 'bg-gray-50', text: 'text-gray-600' };

  const actions = (
    <div className="space-y-3">
        {flagged.size > 0 && !reReviewing && (
          <p className="text-xs text-center text-orange-500">
            {flagged.size} answer{flagged.size > 1 ? 's' : ''} flagged — AI will re-check when you continue
          </p>
        )}
        <button
          onClick={handleContinue}
          disabled={reReviewing}
          className={`w-full py-4 rounded-xl font-bold text-base text-white shadow transition-all ${
            reReviewing
              ? 'bg-gray-400 cursor-not-allowed'
              : isLastRound
              ? 'bg-emerald-600 hover:bg-emerald-700 active:scale-95'
              : 'bg-red-600 hover:bg-red-700 active:scale-95'
          }`}
        >
          {reReviewing ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              Re-checking…
            </span>
          ) : isLastRound ? 'See Final Results' : 'Continue →'}
        </button>
        {!isLastRound && (
          <button
            onClick={handleQuit}
            className="w-full py-3 rounded-xl text-sm font-semibold text-gray-400 hover:text-gray-600 transition-colors"
          >
            End Game
          </button>
        )}
      </div>
  );

  return (
    <div className="min-h-screen bg-orange-50 flex flex-col">
      {confirmDialog}
      <div className="flex-1 flex flex-col w-full lg:max-w-6xl lg:mx-auto lg:px-4">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 text-center lg:px-0 lg:text-left">
        <p className="text-2xl font-bold text-red-600">
          Round {lastRound?.round} Results
        </p>
        <p className="text-lg text-gray-700 mt-0.5">
          Letter{' '}
          <span className="font-bold text-red-600">{lastRound?.letter}</span>
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4 lg:flex-none lg:overflow-visible lg:px-0 lg:pb-8 lg:space-y-0 lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-6 lg:items-start">
        <div className="space-y-4">
        {/* Answers + scores table */}
        <div className="bg-white rounded-2xl shadow-md p-5 lg:p-8">
          <p className="text-base font-semibold text-gray-700 text-center mb-4 lg:text-lg">Round Scores</p>

          <table className="w-full">
            <colgroup>
              <col style={{ width: '22%' }} />
              <col style={{ width: '35%' }} />
              <col style={{ width: '35%' }} />
              <col style={{ width: '8%' }} />
            </colgroup>
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left text-xs font-semibold text-gray-500 pb-2 lg:text-sm">Category</th>
                <th className="text-center text-xs font-semibold text-gray-500 pb-2 lg:text-sm">You</th>
                <th className="text-center text-xs font-semibold text-gray-500 pb-2 lg:text-sm">{botConfig.name}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => {
                const uAns         = lastRound?.userAnswers?.[cat]?.trim() || '';
                const bAns         = lastRound?.botAnswers?.[cat]?.trim()  || '';
                const uScore       = lastRound?.userScores?.[cat] ?? 0;
                const bScore       = lastRound?.botScores?.[cat]  ?? 0;
                const letter       = lastRound?.letter || '';
                const canDispute   = uScore === 0 && uAns && uAns[0]?.toUpperCase() === letter.toUpperCase();
                const alreadyFlagged = flagged.has(cat);
                return (
                  <tr key={cat} className="border-b border-gray-100">
                    <td className="py-3 text-xs font-semibold text-gray-600 capitalize lg:py-4 lg:text-base">
                      {CATEGORY_LABELS[cat]}
                    </td>
                    <td className="py-3 text-center lg:py-4">
                      {uAns ? (
                        <button
                          onClick={() => handleLookup(uAns, cat)}
                          className="text-xs text-gray-600 lg:text-base underline decoration-dotted underline-offset-2 hover:text-red-500 transition-colors block mx-auto mb-0.5"
                        >
                          {uAns}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-300 block mb-0.5">—</span>
                      )}
                      <span className={`text-sm font-semibold lg:text-lg ${scoreColor(uScore)}`}>
                        {uScore > 0 ? `+${uScore}` : uScore}
                      </span>
                    </td>
                    <td className="py-3 text-center lg:py-4">
                      {bAns ? (
                        <button
                          onClick={() => handleLookup(bAns, cat)}
                          className="text-xs text-gray-400 lg:text-base underline decoration-dotted underline-offset-2 hover:text-red-500 transition-colors block mx-auto mb-0.5"
                        >
                          {bAns}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-300 block mb-0.5">—</span>
                      )}
                      <span className={`text-sm font-semibold lg:text-lg ${scoreColor(bScore)}`}>
                        {bScore > 0 ? `+${bScore}` : bScore}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      {canDispute && !alreadyFlagged ? (
                        <button
                          onClick={() => handleFlag(cat)}
                          className="text-xs text-red-400 hover:text-red-600 transition-colors"
                          title="Dispute"
                        >
                          🚩
                        </button>
                      ) : alreadyFlagged ? (
                        <span className="text-xs text-orange-400">Flagged</span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200">
                <td className="pt-3 text-xs font-bold text-gray-700 lg:text-base">Total</td>
                <td className="pt-3 text-center">
                  <span className="text-base font-black text-red-600 lg:text-2xl">
                    +{lastRound?.userTotal ?? 0}
                  </span>
                </td>
                <td className="pt-3 text-center">
                  <span className="text-base font-black lg:text-2xl" style={{ color: botConfig.color || '#6b7280' }}>
                    +{lastRound?.botTotal ?? 0}
                  </span>
                </td>
                <td />
              </tr>
            </tfoot>
          </table>

          {/* Legend */}
          <div className="flex gap-4 mt-3 justify-center">
            <span className="text-xs text-emerald-700">● Valid</span>
            <span className="text-xs text-red-600">● Invalid / 0</span>
          </div>
          {/* Solo doesn't use the 10 / 5 / 0 multiplayer rule; say how it works. */}
          <p className="text-xs text-gray-600 text-center mt-2 leading-relaxed">
            Valid answers score up to 15. The rarer the answer, the more it scores.
            The same answer as {botConfig.name} scores half.
          </p>
        </div>

        {/* Special round effect */}
        {specialEffect && (
          <div className={`rounded-2xl border-2 ${effectStyle.border} ${effectStyle.bg} p-4 flex items-center gap-3`}>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">
                ⚡ Special Round
              </p>
              <p className={`font-bold text-base ${effectStyle.text}`}>{specialEffect.title}</p>
              <p className={`text-sm mt-0.5 ${effectStyle.text}`}>{specialEffect.description}</p>
            </div>
            <div className="flex-shrink-0 -my-2" aria-hidden="true">
              <MascotAvatar
                mascot={myMascot}
                mood={specialIsPenalty ? 'nervous' : 'celebrate'}
                size={myMascot.base === 'pencil' ? 46 : 58}
                shadow={false}
              />
            </div>
          </div>
        )}

        </div>

        <div className="space-y-4 lg:sticky lg:top-4">
        {/* Overall standings */}
        <div className="bg-white rounded-2xl shadow-md p-5">
          <p className="text-base font-bold text-gray-700 text-center mb-4">Standings</p>

          {/* You */}
          <div className="flex items-center justify-between py-3 px-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-lg">{diff >= 0 ? '🥇' : '🥈'}</span>
              <span className="text-base font-semibold text-gray-700">You</span>
            </div>
            <span className="text-xl font-black text-red-600">{userScore}</span>
          </div>

          {/* Bot */}
          <div className="flex items-center justify-between py-3 px-4">
            <div className="flex items-center gap-2">
              <span className="text-lg">{diff <= 0 ? '🥇' : '🥈'}</span>
              <BotMascot bot={selectedBot} state={diff < 0 ? 'won' : diff > 0 ? 'lost' : 'idle'} size={32} />
              <span className="text-base font-semibold text-gray-700">{botConfig.name}</span>
            </div>
            <span className="text-xl font-black" style={{ color: botConfig.color || '#6b7280' }}>
              {botScore}
            </span>
          </div>

          {/* Badge */}
          <div className="mt-3 flex justify-center">
            {diff > 0 ? (
              <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                You're winning!
              </span>
            ) : diff < 0 ? (
              <span className="bg-red-100 text-red-800 text-xs font-semibold px-3 py-1 rounded-full">
                You're behind
              </span>
            ) : (
              <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1 rounded-full">
                It's tied!
              </span>
            )}
          </div>
        </div>

        <div className="hidden lg:block">{actions}</div>
        </div>
      </div>

      {/* Buttons */}
      <div className="px-4 pb-8 pt-3 lg:hidden">{actions}</div>
      </div>

      {/* Dictionary lookup sheet */}
      {lookupWord && (
        <DictionarySheet
          word={lookupWord}
          result={dictResult}
          loading={dictLoading}
          error={dictError}
          onClose={handleCloseLookup}
        />
      )}
    </div>
  );
}
